// Single source of truth for GA4 form events (used by both the legacy jQuery
// form handler and the block-version form validator).
//
// submit-form  — commercial leads ONLY: contact_us_form, cost_calculator, footer_form,
//                project_estimate_form (banner-new block, footer form route + data-form-name).
// content-form — every other form on the site (blog subscription, lead magnets,
//                whitepapers, newsletter popup, HR, partners, KYC, landings...).

export const COMMERCIAL_EVENT = "submit-form";
export const CONTENT_EVENT = "content-form";

// form_name of the subscribe/lead-magnet popup, which has its own submit handler.
export const NEWSLETTER_POPUP_FORM_NAME = "newsletter_popup";

// All maps are keyed by the backend "typeform" / "action" marker that is already
// used for Slack/CRM routing. "typeform" wins when a form carries both.
const COMMERCIAL_FORMS = {
  get_in_touch: "contact_us_form",
  cost_form: "cost_calculator",
  chatbot_calc_form: "cost_calculator",
  contact_form: "footer_form",
};

// Content forms that need a canonical GA4 name. Any other marker falls through
// to content-form under its own name, so nothing is silently dropped.
const CONTENT_FORMS = {
  jsx_handler_subscriber_block_form: "blog_subscription",
  lead_magnet: "blog_lead_magnet",
  guide_form: "whitepaper_form",
  // The subscribe popup posts to the jelvix-subscribers-wp plugin, so its
  // "action" is an AJAX route name and cannot be renamed in the markup.
  jsx_handler_ajax: NEWSLETTER_POPUP_FORM_NAME,
};

// The legacy handler binds to every <form> on the page, so a few controls that
// are not submissions at all would otherwise end up in content-form.
const IGNORED_MARKERS = ["update_case_pagination"];

// Same problem, but for forms that carry no typeform/action marker at all and
// so cannot be filtered by IGNORED_MARKERS — they have to go by selector.
const IGNORED_FORM_SELECTORS = ["form.header-search"];

export function isIgnoredForm(formEl) {
  if (!formEl || typeof formEl.matches !== "function") return false;

  for (let i = 0; i < IGNORED_FORM_SELECTORS.length; i++) {
    if (formEl.matches(IGNORED_FORM_SELECTORS[i])) return true;
  }

  return false;
}

export function resolveFormEvent(marker) {
  if (marker && IGNORED_MARKERS.indexOf(marker) !== -1) return null;

  if (marker && COMMERCIAL_FORMS[marker]) {
    return { event: COMMERCIAL_EVENT, formName: COMMERCIAL_FORMS[marker] };
  }

  return {
    event: CONTENT_EVENT,
    formName: (marker && CONTENT_FORMS[marker]) || marker || "unknown_form",
  };
}

export function pushFormEvent(marker, formEl) {
  if (isIgnoredForm(formEl)) {
    // debugLog("skipped", { marker: marker, reason: "ignored form" });
    return;
  }

  const resolved = resolveFormEvent(marker);

  if (!resolved) {
    // debugLog("skipped", { marker: marker, reason: "ignored marker" });
    return;
  }

  // A form can share the backend marker (Slack/CRM route) of another form but
  // still need its own GA4 name: <form data-form-name="project_estimate_form">. The event
  // itself (submit-form / content-form) is always resolved from the marker.
  const formName = (formEl && formEl.dataset && formEl.dataset.formName) || resolved.formName;

  pushEvent(resolved.event, formName, marker);
}

export function pushEvent(event, formName, marker) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: event, form_name: formName });

  // debugLog("push", {
  //   event: event,
  //   form_name: formName,
  //   marker: marker || formName,
  // });
}

// --- TEMPORARY staging instrumentation -------------------------------------
// Currently disabled so it doesn't ship to production: uncomment this block
// and the debugLog() calls above to bring the staging logging back. Remove it
// all once the submit-form / content-form split has been verified on staging.
// Silence it without a rebuild: window.jxFormAnalyticsDebug = false
// function debugLog(action, payload) {
//   if (window.jxFormAnalyticsDebug === false) return;
//
//   console.log("[form-analytics] " + action, payload);
// }
// --- end TEMPORARY ---------------------------------------------------------
